import { sanitizeSearchInput } from '../utils/security';
import { getCsrfToken } from './authService';
import { buildWebDeviceInfo, setStoredSessionId, getStoredSessionId } from '../utils/deviceInfo';

/** Access token bellekte; sayfa yenilemesinde sessionStorage yedek. */
let accessTokenMem = '';
try {
  accessTokenMem = sessionStorage.getItem('hlm_token_mem') || '';
} catch {
  accessTokenMem = '';
}

let refreshInFlight = null;

const AUTH_REFRESH_PATH = '/api/auth/token-yenile';

/** localhost + yerel ağ IP — CRA dev proxy (/api → REACT_APP_API_URL) */
function isLocalDevHostname(hostname) {
  if (!hostname) return false;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3}$/.test(hostname)) return true;
  return false;
}

/** Production'da nginx üzerinden aynı origin ile proxy edilen frontend hostları */
const SAME_ORIGIN_API_HOSTS = new Set(['servis.unmak.com', 'bayi.unmak.tech', 'unmak.tech']);

/** Production API kökü; backend .env içindeki API_URL ile aynı olmalıdır. */
const DEFAULT_API_BASE_URL =
  (typeof process !== 'undefined' &&
    (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL)) ||
  'https://api.unmak.com';

export const getApiBaseUrl = () => {
  // CRA dev + production panel: istekler aynı origin'e gider; nginx /api → backend proxy.
  if (typeof window !== 'undefined') {
    const { hostname, origin } = window.location;
    if (isLocalDevHostname(hostname) || SAME_ORIGIN_API_HOSTS.has(hostname)) {
      return origin;
    }
  }

  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    return DEFAULT_API_BASE_URL;
  }

  return DEFAULT_API_BASE_URL;
};

export const getStoredToken = () => {
  if (accessTokenMem) return accessTokenMem;
  try {
    return localStorage.getItem('hlm_token') || '';
  } catch {
    return '';
  }
};

export const getStoredRefreshToken = () => {
  try {
    return localStorage.getItem('hlm_refresh_token') || '';
  } catch {
    return '';
  }
};

export const setStoredTokens = ({ token, refreshToken, sessionId }) => {
  try {
    if (token) {
      accessTokenMem = token;
      try {
        sessionStorage.setItem('hlm_token_mem', token);
      } catch {
        /* ignore */
      }
      localStorage.setItem('hlm_token', token);
    } else if (token === null || token === undefined) {
      accessTokenMem = '';
      try {
        sessionStorage.removeItem('hlm_token_mem');
      } catch {
        /* ignore */
      }
      localStorage.removeItem('hlm_token');
    }

    if (refreshToken) {
      localStorage.setItem('hlm_refresh_token', refreshToken);
    } else if (refreshToken === null) {
      localStorage.removeItem('hlm_refresh_token');
    }

    if (sessionId !== undefined) {
      setStoredSessionId(sessionId || null);
    }
  } catch {
    /* ignore */
  }
};

/**
 * Kullanıcı bilgisini localStorage'a kaydet
 */
export const setStoredUser = (user) => {
  try {
    if (user) {
      localStorage.setItem('hlm_user', JSON.stringify(user));
    } else if (user === null || user === undefined) {
      localStorage.removeItem('hlm_user');
    }
  } catch {
    // localStorage erişilemezse sessizce devam et
  }
};

/**
 * localStorage'dan kullanıcı bilgisini getir
 */
export const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem('hlm_user');
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch {
    // Parse hatası durumunda null döndür
  }
  return null;
};

/** Liste araması: strip edilmez; backend regex kaçırır. Uzunluk sınırı abuse önler. */
const ARAMA_QUERY_PARAM_MAX = 200;

const buildQueryString = (params = {}) => {
  const parts = Object.entries(params).flatMap(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return [];
    }
    if (typeof value === 'string') {
      const raw =
        key === 'arama'
          ? value.trim().slice(0, ARAMA_QUERY_PARAM_MAX)
          : sanitizeSearchInput(value);
      if (raw === '') {
        return [];
      }
      return [`${encodeURIComponent(key)}=${encodeURIComponent(raw)}`];
    }
    return [`${encodeURIComponent(key)}=${encodeURIComponent(value)}`];
  });

  const query = parts.join('&');
  return query ? `?${query}` : '';
};


const shouldRetryNetwork = (error, retryCount) => retryCount < 2 && error instanceof TypeError;

export function isAccessTokenFresh(token = getStoredToken(), minRemainingMs = 90 * 1000) {
  if (!token || typeof token !== 'string') return false;
  const parts = token.split('.');
  if (parts.length < 2) return false;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json);
    return (Number(payload.exp) || 0) * 1000 - Date.now() > minRemainingMs;
  } catch {
    return false;
  }
}

function reportClientAuthEvent(neden) {
  try {
    const user = getStoredUser();
    const apiBaseUrl = getApiBaseUrl();
    fetch(`${apiBaseUrl}/api/auth/istemci-oturum-log`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({
        neden,
        kullaniciAdi: user?.kullaniciAdi || null,
        hasAccessToken: !!getStoredToken(),
        hasRefreshToken: !!getStoredRefreshToken(),
        sessionId: getStoredSessionId() || null,
        path: typeof window !== 'undefined' ? window.location.pathname : null,
        href: typeof window !== 'undefined' ? window.location.href : null,
      }),
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

async function performTokenRefresh() {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    const refreshTokenValue = getStoredRefreshToken();
    if (!refreshTokenValue) {
      reportClientAuthEvent('missing_refresh');
      const err = new Error('Refresh token bulunamadı');
      err.status = 401;
      throw err;
    }

    const apiBaseUrl = getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}${AUTH_REFRESH_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        refreshToken: refreshTokenValue,
        deviceInfo: buildWebDeviceInfo(),
      }),
    });

    let data;
    try {
      data = await response.json();
    } catch {
      const err = new Error('Token yenileme yanıtı okunamadı');
      err.status = response.status;
      throw err;
    }

    if (!response.ok || !data?.veri?.token) {
      if (response.status === 429) {
        reportClientAuthEvent('refresh_429');
      }
      const err = new Error(data?.mesaj || 'Token yenileme başarısız');
      err.status = response.status;
      throw err;
    }

    setStoredTokens({
      token: data.veri.token,
      refreshToken: data.veri.refreshToken || refreshTokenValue,
      sessionId: data.veri.sessionId,
    });

    return data;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

const executeApiRequest = async (
  endpoint,
  { method = 'GET', body, headers = {}, query, skipCsrf = false, retryCount = 0, authRetried = false, timeoutMs = 30000 } = {},
) => {
  const token = getStoredToken();
  const apiBaseUrl = getApiBaseUrl();
  const url = `${apiBaseUrl}${endpoint}${query !== undefined ? buildQueryString(query) : ''}`;

  // CSRF token al (skipCsrf false ise)
  let csrfToken = null;
  if (!skipCsrf && (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH')) {
    try {
      csrfToken = await getCsrfToken();
      // CSRF token alınamazsa devam et (endpoint henüz yapılmamış olabilir)
    } catch (error) {
      // CSRF token hatası isteği engellemesin
    }
  }

  const isFormData = body && typeof FormData !== 'undefined' && body instanceof FormData;
  const finalHeaders = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    ...headers,
  };
  if (!isFormData) {
    finalHeaders['Content-Type'] = finalHeaders['Content-Type'] || finalHeaders['content-type'] || 'application/json';
  }
  // FormData için Content-Type eklenmez - tarayıcı boundary ile otomatik ekler

  const options = {
    method,
    headers: finalHeaders,
    credentials: 'include',
  };

  if (body) {
    options.body = isFormData ? body : JSON.stringify(body);
  }

  try {
    let response;
    try {
      // Timeout ekle (30 saniye)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      try {
        response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error(`İstek zaman aşımına uğradı (${timeoutMs / 1000} saniye). Lütfen tekrar deneyin.`);
        }
        throw fetchError;
      }
    } catch (fetchError) {
      // Fetch hatası - genellikle CORS veya network hatası
      // CORS hatası muhtemelen
      if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
        if (shouldRetryNetwork(fetchError, retryCount)) {
          return executeApiRequest(endpoint, { method, body, headers, query, skipCsrf, retryCount: retryCount + 1, authRetried, timeoutMs });
        }
        throw new Error(`CORS veya Network hatası: ${fetchError.message}. API sunucusunun çalıştığından ve CORS ayarlarının doğru olduğundan emin olun. (Origin: ${window.location.origin})`);
      }
      
      throw fetchError;
    }
    
    // CORS hatası kontrolü - response yoksa veya type 'opaque' ise CORS hatası olabilir
    if (!response) {
      throw new Error('Sunucuya bağlanılamadı. CORS ayarlarını kontrol edin.');
    }

    // CORS hatası için kontrol - response.status 0 ise genellikle CORS hatasıdır
    if (response.status === 0 || response.type === 'opaque') {
      throw new Error('CORS hatası: API sunucusunun CORS ayarları kontrol edilmeli. Backend\'de Access-Control-Allow-Origin header\'ı eksik olabilir.');
    }

    // Network hatası kontrolü
    if (response.type === 'error') {
      throw new Error('Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin veya daha sonra tekrar deneyin.');
    }
    

    // Response'u clone et ki tekrar okuyabilsin
    const clonedResponse = response.clone();
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      // Parse hatası durumunda response text'ini oku
      try {
        const text = await clonedResponse.text();
        try {
          data = JSON.parse(text);
        } catch {
          data = { mesaj: text || 'Yanıt parse edilemedi' };
        }
      } catch {
        data = { mesaj: 'Yanıt okunamadı' };
      }
    }

    if (!response.ok) {
      // Backend'den gelen hata mesajını bul
      let errorMessage = data?.mesaj || data?.message || data?.error || data?.hata || data?.validation?.message || `HTTP ${response.status}: ${response.statusText}` || 'İstek başarısız oldu';
      
      // Eğer hatalar array'i varsa, daha detaylı mesaj oluştur (USER_AUTH_V2.md formatı)
      if (data?.hatalar && Array.isArray(data.hatalar) && data.hatalar.length > 0) {
        const hataMesajlari = data.hatalar.map(h => {
          const alan = h.alan || h.field || '';
          const mesaj = h.mesaj || h.message || '';
          return `${alan}: ${mesaj}`;
        }).join(', ');
        errorMessage = `${errorMessage} (${hataMesajlari})`;
      }
      
      // HTML entity'lerini decode et
      if (typeof errorMessage === 'string') {
        errorMessage = errorMessage
          .replace(/&#x27;/g, "'")
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&#39;/g, "'");
      }
      
      // Schema/model hatalarını daha anlaşılır mesaja çevir
      if (typeof errorMessage === 'string' && errorMessage.includes('Schema hasn\'t been registered')) {
        errorMessage = 'Backend\'de bir model hatası oluştu. Lütfen daha sonra tekrar deneyin veya sistem yöneticisine bildirin.';
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = data;

      if (shouldRetryNetwork(error, retryCount)) {
        return executeApiRequest(endpoint, { method, body, headers, query, skipCsrf, retryCount: retryCount + 1, authRetried, timeoutMs });
      }

      if (
        error.status === 401 &&
        !authRetried &&
        endpoint !== AUTH_REFRESH_PATH &&
        !endpoint.startsWith('/api/auth/giris') &&
        !endpoint.startsWith('/api/auth/login')
      ) {
        try {
          await performTokenRefresh();
          return executeApiRequest(endpoint, { method, body, headers, query, skipCsrf, retryCount, authRetried: true, timeoutMs });
        } catch (refreshError) {
          throw refreshError;
        }
      }

      throw error;
    }

    return data;
  } catch (error) {
    if (shouldRetryNetwork(error, retryCount)) {
      return executeApiRequest(endpoint, { method, body, headers, query, skipCsrf, retryCount: retryCount + 1, authRetried, timeoutMs });
    }
    // Fetch network hatası (CORS dahil)
    if (error instanceof TypeError) {
      const errorMsg = error.message.toLowerCase();
      
      if (errorMsg.includes('failed to fetch') || errorMsg.includes('network')) {
        throw new Error('Sunucuya bağlanılamadı. API sunucusunun çalıştığından ve CORS ayarlarının doğru olduğundan emin olun.');
      }
      
      if (errorMsg.includes('cors') || errorMsg.includes('origin')) {
        throw new Error('CORS hatası: API sunucusunda CORS ayarları eksik veya yanlış yapılandırılmış olabilir.');
      }
    }

    
    // Zaten işlenmiş hata
    if (error.message && error.status) {
      throw error;
    }
    
    // Bilinmeyen hata
    throw new Error(error?.message || 'Bilinmeyen bir hata oluştu');
  }
};

export const apiRequest = executeApiRequest;

export const refreshToken = performTokenRefresh;

export const safeApiRequest = async (endpoint, options) => apiRequest(endpoint, options);

export { buildQueryString };


